import http from '@monolith/legacy/services/api/http';
import { captureBrandsException } from '@core/plugins/sentry/helper';
import {
  type ContactsSearchResponse,
  type ContactsSearchParams,
  type ContactsExportParams,
  type ContactsMultiSearchParams,
  type ContactSearchData,
  type ContactFilter,
  type ValueContactFilter,
  ContactFilterType,
  type FiltersResponse,
} from '../../types/api.types';
import type { AxiosResponse } from 'axios';
import { memoize } from '@core/utilities/lodash-utils';
import { deserialize } from '@monolith/legacy/services/utils/jsonapi-parser';
import { Sort } from '@monolith/legacy/types/sorting';

const ROOT_API_PATH = '/api/internal/v1/network/contacts';

const jsonApiConfigs = {
  headers: {
    'Content-Type': 'application/vnd.api+json',
  },
};

export const getContacts = async ({
  searchQuery = '',
  facets = [],
  filters = {},
  sort = {},
  currentPage = 1,
  size = 100,
}: {
  searchQuery?: string;
  facets?: string[];
  filters?: Record<string, ContactFilter>;
  sort?: Record<string, Sort>;
  currentPage?: number;
  size?: number;
} = {}): Promise<ContactsSearchResponse> => {
  const apiPayload: ContactsSearchParams = {
    data: {
      type: 'search-params',
      attributes: {
        facets,
        filters: Object.values(filters) || [],
        sort,
        page: {
          size,
          current: currentPage,
        },
      },
    },
  };

  if (searchQuery) {
    apiPayload.data.attributes.query = { term: searchQuery };
  }

  const apiRoute = `${ROOT_API_PATH}/search`;

  try {
    const response: AxiosResponse<ContactsSearchResponse> = await http().post(apiRoute, apiPayload, jsonApiConfigs);

    return response.data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'contact-management/api' },
      { label: 'action', value: 'getContacts' },
    ]);
    return Promise.reject(error);
  }
};

export const getContactsMultiSearch = async (dataObj: ContactSearchData[] = []): Promise<ContactsSearchResponse> => {
  const apiPayload: ContactsMultiSearchParams = {
    data: dataObj,
  };

  const apiRoute = `${ROOT_API_PATH}/multi-search`;

  try {
    const response: AxiosResponse<ContactsSearchResponse> = await http().post(apiRoute, apiPayload, jsonApiConfigs);
    return deserialize(response.data).data ?? [];
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'contact-management/api' },
      { label: 'action', value: 'getContactsMultiSearch' },
    ]);
    return Promise.reject(error);
  }
};

export const getFilters = memoize(async (): Promise<FiltersResponse> => {
  const apiRoute = `${ROOT_API_PATH}/search/widgets`;
  try {
    const response: AxiosResponse<FiltersResponse> = await http().get(apiRoute, jsonApiConfigs);

    return response.data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'contact-management/api' },
      { label: 'action', value: 'getFilters' },
    ]);
    return Promise.reject(error);
  }
});

export const deleteContact = async (contactId: string): Promise<void> => {
  try {
    const response: AxiosResponse = await http().delete(`${ROOT_API_PATH}/${contactId}`);
    return response.data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'contact-management/api' },
      { label: 'action', value: 'deleteContact' },
    ]);
    return Promise.reject(error);
  }
};

export const deleteContacts = async (payload: Record<'id', string>[]): Promise<Blob> => {
  try {
    const apiRoute = `${ROOT_API_PATH}/delete`;
    const apiPayload = { contacts: payload };

    const response: AxiosResponse<Blob> = await http().post(apiRoute, apiPayload, jsonApiConfigs);
    return response.data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'contact-management/api' },
      { label: 'action', value: 'deleteContacts' },
    ]);
    return Promise.reject(error);
  }
};

export const exportContacts = async ({
  searchQuery = '',
  filters = {},
  sort = {},
}: {
  searchQuery?: string;
  filters?: Record<string, ContactFilter>;
  sort?: Record<string, Sort>;
} = {}): Promise<Blob> => {
  const apiPayload: ContactsExportParams = {
    data: {
      type: 'contact-export',
      attributes: {
        filters: typeof filters === 'object' && filters !== null ? Object.values(filters) : [],
        sort: sort ?? {},
      },
    },
  };

  if (searchQuery) {
    apiPayload.data.attributes.query = { term: searchQuery };
  }

  const apiRoute = `${ROOT_API_PATH}/exports`;

  try {
    const { data } = await http().post(apiRoute, apiPayload, jsonApiConfigs);
    return Promise.resolve(data);
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'contact-management/api' },
      { label: 'action', value: 'exportContacts' },
    ]);
    return Promise.reject(error);
  }
};

// Exported function for exporting My Network contacts
export const exportMyNetworkContacts = async (params: Parameters<typeof exportContacts>[0] = {}): Promise<Blob> => await exportContacts(params);

// Main exported function for My Network contacts
export const getMyNetworkContacts = async (params: Parameters<typeof getContacts>[0] = {}): Promise<ContactsSearchResponse> => {
  const mergedFilters = { ...params?.filters };

  // Custom Segment is not supported by API, removing it from filters if present
  if (mergedFilters['segment']?.type === ContactFilterType.Value) {
    const valueFilter = mergedFilters['segment'] as ValueContactFilter;
    if (Array.isArray(valueFilter.value) && valueFilter.value.some((value: string | number) => value === 'custom')) {
      delete mergedFilters['segment'];
    }
  }

  // An ankorcrm segment reference is resolved server-side at send time; the
  // monolith contact search does not know the attribute, so it never travels
  // on preview searches (its companion `uuid` preview filter does).
  delete mergedFilters['segmentUuid'];

  return await getContacts({
    ...params,
    filters: mergedFilters,
  });
};

// Including Prospects and existing Contacts - exporting the getContacts method with descriptive name for clarity while keeping getContacts used only in the module
export const getMyNetworkContactsAndProspects = async (params: Parameters<typeof getContacts>[0] = {}): Promise<ContactsSearchResponse> => getContacts(params);

export const getMyNetworkContactsMultiSearch = async (dataObj: ContactSearchData[] = []): Promise<ContactsSearchResponse> => await getContactsMultiSearch(dataObj);
